/**
 * Create-organization modal, opened from the UserMenu.
 *
 * A user who already belongs to one or more orgs needs a way to spin up
 * another and move into it — the full-screen onboarding form
 * (CreateOrgScreen in main.tsx) only renders for users with zero orgs.
 * On success, AuthContext.createOrg activates the new org and re-fetches
 * /me, so the cloud shell remounts on the new store and this dialog just
 * closes.
 */

import { useEffect, useId, useRef, useState } from 'react';

import { useAuth } from '../auth/AuthContext.js';

import './ProjectSettings.css';

interface Props {
  onClose: () => void;
}

export function CreateOrgDialog({ onClose }: Props): JSX.Element {
  const auth = useAuth();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const nameId = useId();
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const d = dialogRef.current;
    if (d && !d.open) {
      d.showModal();
      window.setTimeout(() => nameInputRef.current?.focus(), 0);
    }
  }, []);

  // Native dialog Esc → cancel.
  useEffect(() => {
    const d = dialogRef.current;
    if (!d) return;
    const handler = (): void => onClose();
    d.addEventListener('cancel', handler);
    return () => d.removeEventListener('cancel', handler);
  }, [onClose]);

  const handleSubmit = async (): Promise<void> => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Organization name is required.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await auth.createOrg(trimmed);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create organization.');
      setBusy(false);
    }
  };

  return (
    <dialog ref={dialogRef} className="project-settings-dialog" aria-labelledby="co-title">
      <form
        method="dialog"
        className="project-settings-form"
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
      >
        <header className="project-settings-header">
          <h2 id="co-title" className="project-settings-title">
            Create organization
          </h2>
          <p className="project-settings-description">
            You&apos;ll be the owner of the new organization and switched into it.
          </p>
        </header>

        <div className="project-settings-body">
          <div className="landing-field">
            <label className="landing-label" htmlFor={nameId}>
              Organization name
            </label>
            <input
              ref={nameInputRef}
              id={nameId}
              type="text"
              className="input"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setError(null);
              }}
              placeholder="e.g. ACME Corp."
              autoComplete="off"
              spellCheck={false}
            />
          </div>
          {error && (
            <p className="project-settings-error" role="alert">
              {error}
            </p>
          )}
        </div>

        <footer className="project-settings-footer">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={busy || name.trim() === ''}>
            {busy ? 'Creating…' : 'Create organization'}
          </button>
        </footer>
      </form>
    </dialog>
  );
}
